import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Whale — a rounded, smiling whale calf resting on its belly like a plush toy:
// one big blue blob of a body, a cream belly, tiny side fins, a flat tail and
// a jaunty water spout frozen above its blowhole. Storybook batch: chunky toy
// proportions, big friendly eyes close together, warm saturated colours.
//
// Frame: faces +Z (the eyes and smile), rests on the ground at y = 0 (belly
// contact — a beached-but-happy calf, no water plane). ~1.6 m long, ~0.7 m tall.
//
// Rig (flat, each named node about its own pivot):
//   - "body"  : the whole blob + face. Breathes up and down.
//   - "tail"  : tail stock + flukes. Pivot at the tail root, sways and pats.
//   - "fin-l" / "fin-r" : the stubby side fins. Pivot at each shoulder.
//   - "spout" : the water column + droplets. Scales about the blowhole so it
//               puffs up from nothing and shrinks away.

const BLUE = "#4f8fd6";        // main hide — cheerful storybook blue
const BLUE_DARK = "#3a6ea8";   // shading (flukes, fin undersides, back)
const CREAM = "#f6efe3";       // belly + eye highlights
const EYE_DARK = "#3b3230";    // pupils, smile, blowhole (warm dark, not black)
const WATER = "#bfe6f2";       // the spout — pale splashy aqua

// Pivots for the animation tracks.
const TAIL_PIVOT: [number, number, number] = [0, 0.3, -0.55];
const FIN_L: [number, number, number] = [-0.33, 0.24, 0.35];
const FIN_R: [number, number, number] = [0.33, 0.24, 0.35];
const BLOWHOLE: [number, number, number] = [0, 0.63, 0.28];

function fin(name: string, side: -1 | 1): Part {
  // A stubby flattened flipper angled slightly down from the shoulder.
  return {
    geometry: new THREE.SphereGeometry(0.12, 10, 8),
    color: BLUE_DARK,
    position: [side * 0.38, 0.22, 0.35],
    scale: [1.5, 0.3, 0.85],
    rotation: [0, side * -0.35, side * -0.4],
    name,
  };
}

export const whale = defineModel({
  id: "whale",
  batchId: "mess",
  name: "Whale",
  tags: ["creature", "mess", "cute", "animated", "sea", "ocean", "whale", "animal"],
  summary: "A smiling whale calf resting on its belly, puffing a little water spout.",
  description:
    "A storybook whale calf: one chunky blue blob resting belly-down like a " +
    "plush toy, with a cream belly, big close-set eyes, tiny side fins, a flat " +
    "tail and a splashy water spout above its blowhole. The idle breathes and " +
    "pats the tail; spout-puff sends the spout puffing up and shrinking away.",
  author: "models",
  footprint: 0.9,
  tiles: { w: 1, h: 2 },
  parts(): Part[] {
    return [
      // ── the body blob ────────────────────────────────────────────────────
      // Main mass: one big sphere stretched along Z, bottom kissing the ground.
      { geometry: new THREE.SphereGeometry(0.35, 14, 12), color: BLUE,
        position: [0, 0.34, 0.05], scale: [1.05, 0.9, 2.0], name: "body" },
      // Chunky head boost at the front so the calf reads big-headed.
      { geometry: new THREE.SphereGeometry(0.3, 14, 12), color: BLUE,
        position: [0, 0.36, 0.42], scale: [1.12, 1.0, 1.05], name: "body" },
      // Cream belly: slightly lower so it shows along the ground contact.
      { geometry: new THREE.SphereGeometry(0.34, 14, 12), color: CREAM,
        position: [0, 0.27, 0.14], scale: [0.98, 0.78, 1.85], name: "body" },

      // ── the face (big and close together) ───────────────────────────────
      // Eye whites.
      { geometry: new THREE.SphereGeometry(0.075, 12, 10), color: CREAM,
        position: [-0.12, 0.46, 0.66], name: "body" },
      { geometry: new THREE.SphereGeometry(0.075, 12, 10), color: CREAM,
        position: [0.12, 0.46, 0.66], name: "body" },
      // Dark pupils, set slightly forward.
      { geometry: new THREE.SphereGeometry(0.038, 10, 8), color: EYE_DARK,
        position: [-0.115, 0.46, 0.725], name: "body" },
      { geometry: new THREE.SphereGeometry(0.038, 10, 8), color: EYE_DARK,
        position: [0.115, 0.46, 0.725], name: "body" },
      // Tiny cream highlights for sparkle.
      { geometry: new THREE.SphereGeometry(0.014, 8, 6), color: CREAM,
        position: [-0.098, 0.478, 0.755], name: "body" },
      { geometry: new THREE.SphereGeometry(0.014, 8, 6), color: CREAM,
        position: [0.132, 0.478, 0.755], name: "body" },
      // Smile: a thin dark torus lying almost flat, sunk into the face so only
      // its front arc shows as a wide upturned mouth line.
      { geometry: new THREE.TorusGeometry(0.13, 0.013, 8, 24), color: EYE_DARK,
        position: [0, 0.33, 0.62], rotation: [Math.PI / 2 + 0.18, 0, 0], name: "body" },
      // Blowhole: a small dark stub on the crown, where the spout roots.
      { geometry: new THREE.CylinderGeometry(0.035, 0.045, 0.03, 10), color: EYE_DARK,
        position: [0, 0.635, 0.28], name: "body" },

      // ── the tail ─────────────────────────────────────────────────────────
      // Tail stock reaching back out of the body.
      { geometry: new THREE.SphereGeometry(0.12, 10, 8), color: BLUE,
        position: [0, 0.3, -0.62], scale: [0.9, 0.75, 1.6], name: "tail" },
      // Two flat flukes fanned out horizontally.
      { geometry: new THREE.SphereGeometry(0.16, 10, 8), color: BLUE_DARK,
        position: [-0.15, 0.3, -0.84], scale: [1.5, 0.22, 1.0], rotation: [0, 0.5, 0], name: "tail" },
      { geometry: new THREE.SphereGeometry(0.16, 10, 8), color: BLUE_DARK,
        position: [0.15, 0.3, -0.84], scale: [1.5, 0.22, 1.0], rotation: [0, -0.5, 0], name: "tail" },

      // ── the fins ─────────────────────────────────────────────────────────
      fin("fin-l", -1),
      fin("fin-r", 1),

      // ── the spout ────────────────────────────────────────────────────────
      // Water column: an inverted cone rising from the blowhole.
      { geometry: new THREE.ConeGeometry(0.09, 0.24, 10), color: WATER,
        position: [0, 0.78, 0.28], rotation: [Math.PI, 0, 0], name: "spout" },
      // Splash droplets crowning the column.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: WATER,
        position: [0, 0.94, 0.28], name: "spout" },
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: WATER,
        position: [-0.1, 0.9, 0.28], name: "spout" },
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: WATER,
        position: [0.1, 0.9, 0.28], name: "spout" },
    ];
  },
  animations: [
    // 1. Idle — a slow breathing bob of the blob, the tail patting gently and
    //    the fins giving tiny flaps. The spout stays small and shimmery.
    {
      id: "idle",
      label: "Idle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "body",
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.8, value: [-0.12, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
            { t: 2.4, value: [-0.12, 0, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "fin-l", pivot: FIN_L,
          rotation: [
            { t: 0, value: [0, 0, -0.08], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.14], ease: "sine" },
            { t: 3.2, value: [0, 0, -0.08], ease: "sine" },
          ],
        },
        {
          target: "fin-r", pivot: FIN_R,
          rotation: [
            { t: 0, value: [0, 0, 0.08], ease: "sine" },
            { t: 1.6, value: [0, 0, -0.14], ease: "sine" },
            { t: 3.2, value: [0, 0, 0.08], ease: "sine" },
          ],
        },
        {
          target: "spout", pivot: BLOWHOLE,
          scale: [
            { t: 0, value: 0.85, ease: "sine" },
            { t: 1.6, value: 1.0, ease: "sine" },
            { t: 3.2, value: 0.85, ease: "sine" },
          ],
        },
      ],
    },

    // 2. Spout-puff — the signature move: the spout puffs up from almost
    //    nothing, overshoots, and shrinks away while the body gives a happy
    //    little squash-and-pop and the fins flick.
    {
      id: "spout-puff",
      label: "Spout puff",
      duration: 2.4,
      loop: true,
      tracks: [
        {
          target: "spout", pivot: BLOWHOLE,
          scale: [
            { t: 0, value: 0.1, ease: "easeIn" },
            { t: 0.5, value: 1.25, ease: "easeOut" },
            { t: 0.9, value: 1.0, ease: "sine" },
            { t: 1.6, value: 1.1, ease: "sine" },
            { t: 2.4, value: 0.1, ease: "easeIn" },
          ],
        },
        {
          // The body squashes down to gather, then pops up as it blows.
          target: "body", pivot: [0, 0, 0.05],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 0.25, value: [1.04, 0.94, 1.02], ease: "easeOut" },
            { t: 0.6, value: [0.98, 1.05, 0.99], ease: "easeOut" },
            { t: 1.2, value: [1, 1, 1], ease: "sine" },
            { t: 2.4, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "fin-l", pivot: FIN_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.5, value: [0, 0, 0.45], ease: "easeOut" },
            { t: 1.1, value: [0, 0, 0], ease: "sine" },
            { t: 2.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "fin-r", pivot: FIN_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.5, value: [0, 0, -0.45], ease: "easeOut" },
            { t: 1.1, value: [0, 0, 0], ease: "sine" },
            { t: 2.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.2, 0, 0], ease: "easeOut" },
            { t: 1.2, value: [0, 0, 0], ease: "sine" },
            { t: 2.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
